import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { createForm } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { Page } from '@components/page-containers/page-container';
import { RouteErrorBoundary } from '@components/route-error-boundary';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { TimeSavedData } from 'api/journeys/requests';
import { OutOfCreditsBanner } from 'components/out-of-credits-banner';
import { DurationFields, normalizeDuration } from 'ui/actions/duration';

import { useFeature } from './hooks';

type RouteList = readonly {
  name: string;
  path: string;
  page: JSX.Element;
  hideNavbar?: boolean;
  pageTitle?: string;
  commandPaletteName?: string;
}[];

type RoutePaths<T extends RouteList> = {
  [K in T[number] as K['name']]: K['path'];
};

type RouteData = {
  routes: RouteList;
  prefix?: string;
};

const extractRoutesFromData = <T extends RouteData>(data: T): RoutePaths<T['routes']> =>
  data.routes.reduce(
    (obj, route) => ({ ...obj, [route.name]: (data.prefix ?? '/') + route.path }),
    {} as RoutePaths<T['routes']>
  );

export const getRoutePaths = <T extends RouteData[]>(data: T): RoutePaths<T[number]['routes']> =>
  data.reduce(
    (obj, data) => ({ ...obj, ...extractRoutesFromData(data) }),
    {} as RoutePaths<T[number]['routes']>
  );

type RouteMapperProps = {
  routes: RouteList;
  defaultRoute?: RouteList[number]['path'];
};

export const RouteMapper = ({ routes, defaultRoute }: RouteMapperProps) => {
  const location = useLocation();

  return (
    <Page>
      <OutOfCreditsBanner />
      <Routes>
        {routes.map((route) => (
          <Route
            key={route.name}
            path={route.path}
            element={route.page}
            errorElement={<RouteErrorBoundary />}
          />
        ))}
        {defaultRoute ? (
          <Route
            path="*"
            element={<Navigate to={defaultRoute} state={{ from: location }} replace />}
            errorElement={<RouteErrorBoundary />}
          />
        ) : null}
      </Routes>
    </Page>
  );
};

const Form = createForm<TimeSavedData>();

export const TimeSavedContainer = (props: Partial<TimeSavedData>) => {
  const { days, hours, minutes } = props;
  const defaults = normalizeDuration({
    days: days?.toString(),
    hours: hours?.toString(),
    minutes: minutes?.toString(),
  });
  const { isEnabled: hasTimeSavedEnabled } = useFeature('FEATURE_TIME_SAVED');
  const { t } = useScopedTranslate(locale);

  return hasTimeSavedEnabled ? (
    <>
      <HideContainer title={t('TimeSaved')} defaultClosed>
        <Stack space={4}>
          <DurationFields form={Form} defaults={defaults} />
          <Text text={t('timeSavedInfo')} font="semibold14" />
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
    </>
  ) : null;
};

const locale = {
  en: {
    TimeSaved: 'Time saved',
    timeSavedInfo: 'How much time will this action save you?',
  },
};
